// ---------------------------------------------------------------------------
// LE CATALOGUE : PLUSIEURS ŒUVRES, ET NON PLUS UNE SEULE SÉRIE
//
// D'où l'on vient. Le lecteur d'origine ne savait regarder QU'UNE chose : une
// série, chargée au démarrage dans la variable `serie`, et tout le reste du
// client lisait cette variable-là. Le nom de l'app, la présence Discord,
// l'écran d'attente, la barre du lecteur, la recherche — quarante endroits
// supposaient qu'il n'y avait rien d'autre à regarder. « Bleach » était même
// écrit en dur dans le repli de la présence, ce qui annonçait cette série-là à
// tout un vocal qui regardait un film.
//
// Ce module est la charnière. Il traduit ce que le serveur sert désormais —
// un CATALOGUE d'œuvres, et une œuvre à la fois quand on en ouvre une — vers
// la forme que le reste du client sait déjà lire. Deux raisons de faire la
// bascule ici plutôt que d'aller réécrire les quarante endroits :
//
//   · Une conversion à UN seul endroit se prouve. Quarante lectures adaptées
//     une à une, non : la quarantième aurait gardé l'ancienne forme, et le
//     bug se serait vu en séance, pas ici.
//   · Ce fichier ne connaît ni le DOM, ni le réseau, ni Discord. Il se lit
//     donc en entier depuis Node — c'est ce que fait `essais/oeuvres.mjs`, et
//     c'est pour cela que chaque fonction ci-dessous rend une valeur au lieu
//     de peindre quelque chose.
//
// LE CONTRAT DU SERVEUR, mot pour mot, parce que tout le fichier en dépend :
//
//   GET /api/catalogue    -> { oeuvres: [{ id, titre, type, affiche, fond,
//                                          note, annee, genres, resume }] }
//   GET /api/oeuvre/:id   -> l'œuvre ci-dessus + saisons[] et collections[]
//   GET /api/reprendre    -> { entrees: [{ oeuvreId, titre, saison, episode,
//                                          position, duree, vignette }] }
//
// ⚠️ `duree` EST EN SECONDES, ET C'EST LA VRAIE DURÉE MESURÉE. Jamais un
// arrondi, jamais des minutes. Tout ce fichier la traite comme telle, et
// `dureeLisible` est le seul endroit qui a le droit de la mettre en forme —
// voir l'incident consigné juste au-dessus d'elle.
// ---------------------------------------------------------------------------

import { normaliserRecherche } from './menu-episodes.js';

/** Un nombre strictement positif, ou `null`. Ne devine jamais une valeur. */
const nombreOuNul = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * L'année d'une œuvre, quelle que soit la forme que la source lui donne.
 *
 * ⚠️ `Number('2026-')` VAUT `NaN`, ET C'EST TOUT LE DEFAUT.
 *
 * Un film porte « 2026 ». Une série porte « 2026- » tant qu'elle continue, ou
 * « 2006-2010 » quand elle est finie : c'est le format du protocole, pas une
 * anomalie. Passées à `Number`, ces deux formes rendaient `null` — donc AUCUNE
 * série ni AUCUN anime n'avait d'année.
 *
 * Deux écrans en dépendaient sans le dire : « Nouveautés », qui écarte ce qui
 * n'a pas d'année et n'affichait donc que des films — « dans nouveautés il y a
 * quasiment que des films » —, et le tri par date, qui les rangeait toutes
 * ensemble à la fin.
 *
 * On garde la PREMIERE année : c'est celle de la sortie, et c'est ce qu'on
 * cherche quand on trie du plus récent au plus ancien.
 */
/**
 * L'annee de FIN d'une plage, ou `null` quand il n'y en a pas.
 *
 * `null` a deux sens qu'il faut distinguer avec `estEnCours` : une serie qui
 * continue (« 2026- ») et une oeuvre qui n'a qu'une date (« 2019 », un film).
 * La premiere n'a pas encore de fin, la seconde finit le jour ou elle sort.
 */
const anneeDeFin = (v) => {
  const t = String(v ?? '').trim();
  const m = /(\d{4})\s*[-–]\s*(\d{4})/.exec(t);
  if (m) return Number(m[2]);
  if (/\d{4}\s*[-–]\s*$/.test(t)) return null; // en cours : pas de fin
  return anneeDeTexte(t); // une seule annee : elle est aussi la fin
};

/** Vrai quand la plage reste ouverte : « 2026- ». */
const estEnCours = (v) => /\d{4}\s*[-–]\s*$/.test(String(v ?? '').trim());

const anneeDeTexte = (v) => {
  if (typeof v === 'number') return Number.isFinite(v) && v > 0 ? v : null;
  const m = /(\d{4})/.exec(String(v ?? ''));
  const n = m ? Number(m[1]) : NaN;
  return Number.isFinite(n) && n > 1800 ? n : null;
};

/** Une date (ISO ou ms) en millisecondes, ou `null`. */
const dateOuNul = (v) => {
  const t = typeof v === 'number' ? v : Date.parse(String(v ?? ''));
  return Number.isFinite(t) && t > 0 ? t : null;
};

/** Un nombre positif ou zéro — pour une position de lecture, où 0 a un sens. */
const secondes = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const texteOuVide = (v) => (typeof v === 'string' ? v.trim() : '');
const listeDeTextes = (v) => (Array.isArray(v) ? v.map(texteOuVide).filter(Boolean) : []);

// ---------------------------------------------------------------------------
// Mise en forme d'une durée
// ---------------------------------------------------------------------------

/**
 * Une durée en secondes, écrite comme on la dit.
 *
 * ⚠️ DÉFAUT CORRIGÉ, ET IL FAUT SAVOIR CE QU'IL COÛTAIT. La console de séance
 * affichait `${Math.round(duree / 60)} min` (main.js, ancien peindreGestion) :
 * un épisode de 24 min 33 s devenait « 25 min », et un autre de 24 min 29 s
 * devenait « 24 min ». Deux épisodes de la même durée à quatre secondes près
 * s'affichaient donc avec une minute d'écart, et surtout : la présence Discord,
 * elle, montre « 00:14 / 24:33 » — la vraie valeur, celle que le serveur a
 * MESURÉE. Le même épisode annonçait deux durées différentes à deux
 * centimètres d'écart, et c'est le genre de détail qui fait douter du reste.
 *
 * La règle tient en une phrase : on n'arrondit jamais une durée mesurée, on la
 * découpe. Les secondes ne s'affichent que si elles existent — « 24 min » tout
 * rond reste « 24 min », pas « 24 min 00 », qui se lirait comme une précision
 * qu'on n'a pas.
 *
 *   dureeLisible(1473) === '24 min 33'
 *   dureeLisible(1440) === '24 min'
 *   dureeLisible(6312) === '1 h 45 min 12'
 *   dureeLisible(3600) === '1 h'
 *   dureeLisible(48)   === '48 s'
 *   dureeLisible(0)    === ''
 */
export function dureeLisible(sec) {
  const total = Math.round(secondes(sec));
  if (!total) return '';

  const h = Math.floor(total / 3600);
  const min = Math.floor((total % 3600) / 60);
  const s = total % 60;

  // Moins d'une minute : les secondes seules. « 0 min 48 » annoncerait une
  // durée en minutes là où il n'y en a pas.
  if (!h && !min) return `${s} s`;

  const bouts = [];
  if (h) bouts.push(`${h} h`);
  if (min) bouts.push(`${min} min`);
  // Les secondes se collent à la dernière unité, sans la répéter : « 24 min 33 »
  // et non « 24 min 33 s » — c'est ainsi qu'on lit une durée de vidéo.
  if (s) bouts.push(String(s).padStart(bouts.length ? 2 : 1, '0'));
  return bouts.join(' ');
}

/** « 24:33 », « 1:05:12 » — la forme compacte, pour une jauge ou une pastille. */
export function horloge(sec) {
  const total = Math.round(secondes(sec));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const deux = (n) => String(n).padStart(2, '0');
  return h ? `${h}:${deux(m)}:${deux(s)}` : `${m}:${deux(s)}`;
}

// ---------------------------------------------------------------------------
// Normalisation
//
// Tout ce qui entre ici vient du réseau, donc de nulle part : un champ peut
// manquer, arriver en chaîne là où on attend un nombre, ou porter un type
// inconnu. On ne se protège pas par politesse — une seule propriété absente
// suffisait, dans la version d'origine, à faire lever tout un rendu et à
// laisser un écran vide sans un mot d'explication.
// ---------------------------------------------------------------------------

// TROIS RAYONS DISJOINTS, ET C'EST VOULU.
//
// Toji : « on n'a pas d'anime dans le tri "séries", et on a simplement
// "séries", "films" et "animes" ».
//
// Un anime n'est donc PAS une série qui porterait un genre « Animation » : il
// a son propre rayon, et le filtre « Séries » ne doit jamais en montrer un.
// C'est la différence avec un catalogue à la Netflix, où tout est série ou
// film et où l'animation n'est qu'une étiquette de plus — étiquette qu'il faut
// alors penser à décocher pour ne pas la voir.
const TYPES = new Set(['serie', 'film', 'anime']);

/**
 * Le classement propre aux animes, lu où qu'il se trouve.
 *
 * Le serveur peut le poser dans un objet `anime` ou à plat sur l'œuvre ; on
 * accepte les deux plutôt que d'imposer une forme, parce que ce contrat-là
 * s'écrit en ce moment même de l'autre côté.
 */
const classementAnime = (o) => ({
  format: o?.anime?.format ?? o?.format ?? null,
  saison: o?.anime?.saison ?? o?.saison ?? null,
  statut: o?.anime?.statut ?? o?.statut ?? null,
});

/**
 * Les valeurs de sous-tri réellement présentes dans le catalogue.
 *
 * Comme pour les genres : proposer « OVA » quand aucun n'existe donne un
 * filtre qui ne rend rien, et on cherche alors la panne au mauvais endroit.
 */
/**
 * Cette oeuvre est-elle en UNE SEULE PIECE ?
 *
 * ⚠️ « ANIME » EST UN RAYON, PAS UNE FORME. Le rayon des animes range des
 * series ET des longs metrages : tester `type === 'film'` laissait donc les
 * films d'animation sans bouton de lecture et devant une grille d'episodes
 * vide — « Aucun episode liste pour cette oeuvre », sur un film qui n'en a pas
 * et n'en aura jamais. C'est le format qui tranche, quand il est declare.
 */
export const estUnFilm = (o) => (
  o?.type === 'film' || /^(film|movie|long[- ]m[ée]trage|ova film)$/i.test(String(o?.anime?.format ?? '').trim())
);

/**
 * Ce qu'on ecrit sous une affiche : « Anime », « Film », « Serie ».
 *
 * ⚠️ TOUT CE QUI N'ETAIT PAS UN FILM S'APPELAIT « SERIE ».
 *
 * Toji : « la je suis dans anime et pourtant j'ai ca » — une carte du rayon des
 * animes portant la mention « Serie ». Le filtre etait juste ; c'est l'etiquette
 * qui ne connaissait que deux mots, alors que le catalogue en distingue trois.
 * Une carte doit dire ce qu'elle est, surtout dans un rayon qui l'a deja triee.
 *
 * Le format precise quand il apporte quelque chose : un anime en film ou en OVA
 * n'est pas la meme chose qu'une serie de vingt-quatre episodes, et c'est
 * exactement ce qu'on cherche a savoir avant de cliquer.
 */
export function typeLisible(o) {
  if (o?.type === 'anime') {
    const format = String(o?.anime?.format ?? '').trim();
    // « Serie » ne repete rien d'utile a cote d'« Anime » : on ne le pose que
    // lorsqu'il distingue — un film, une OVA, un special.
    if (format && !/^(s[ée]rie|series|tv)$/i.test(format)) return `Anime · ${format}`;
    return 'Anime';
  }
  return estUnFilm(o) ? 'Film' : 'Série';
}

/**
 * La forme d'un anime, ramenee a DEUX mots : « serie » ou « film ».
 *
 * ⚠️ LA SOURCE EN CONNAIT SIX — TV, Movie, OVA, ONA, Special, Music — ET
 * C'EST TROP. Toji (12/09) : « les animés séparés avec dedans le sous-tri
 * séries / films ». Une OVA, un special, un ONA sont des series au sens de
 * « plusieurs episodes » ; seul ce que `estUnFilm` reconnait est un film. Les
 * valeurs brutes restent dans `anime.format` (l'etiquette de la carte les lit
 * encore) ; c'est le FILTRE qui ne voit plus que deux formes.
 */
export const formeAnime = (o) => (estUnFilm(o) ? 'film' : 'serie');
export const FORMES_ANIME = ['serie', 'film'];

export function sousTrisAnime(oeuvres) {
  const saisons = new Set();
  const statuts = new Set();
  for (const o of oeuvres ?? []) {
    if (o?.type !== 'anime') continue;
    const c = classementAnime(o);
    if (c.saison) saisons.add(String(c.saison));
    if (c.statut) statuts.add(String(c.statut));
  }
  return {
    // Deux formes, toujours les memes : le menu « Format » du rayon des animes
    // ne depend plus de ce que la source a bien voulu declarer.
    formats: [...FORMES_ANIME],
    // Les saisons de diffusion se lisent de la plus récente à la plus
    // ancienne : c'est « ce qui sort en ce moment » qu'on cherche.
    saisons: [...saisons].sort((a, b) => b.localeCompare(a, 'fr')),
    statuts: [...statuts].sort((a, b) => a.localeCompare(b, 'fr')),
  };
}

/** Un épisode du contrat, ramené à une forme sûre. */
function normaliserEpisode(brut, saison, rang) {
  if (!brut || typeof brut !== 'object') return null;
  const numero = nombreOuNul(brut.numero ?? brut.episode) ?? rang + 1;
  return {
    saison,
    numero,
    // Le numéro dont on PARLE quand la série en a un : « l'épisode 412 », pas
    // « S17E46 ». Absent le plus souvent — on ne l'invente pas.
    absolu: nombreOuNul(brut.absolu),
    titre: texteOuVide(brut.titre ?? brut.title),
    resume: texteOuVide(brut.resume),
    // ⚠️ EN SECONDES, telle que le serveur l'a mesurée. On ne la convertit pas,
    // on ne l'arrondit pas : `dureeLisible` s'en charge à l'affichage, et elle
    // seule.
    duree: secondes(brut.duree),
    released: texteOuVide(brut.released) || null,
    vignette: texteOuVide(brut.vignette ?? brut.thumbnail) || null,
    // Les langues telles que le balayage les a vues. On garde les étiquettes
    // brutes : c'est `drapeauxDe` qui sait les lire, et lui seul.
    langues: listeDeTextes(brut.langues),
  };
}

/** Une saison du contrat, ramenée à une forme sûre. */
function normaliserSaison(brut, rang) {
  const numero = nombreOuNul(brut?.numero ?? brut?.saison) ?? rang + 1;
  const episodes = (Array.isArray(brut?.episodes) ? brut.episodes : [])
    .map((e, i) => normaliserEpisode(e, numero, i))
    .filter(Boolean);
  return {
    numero,
    titre: texteOuVide(brut?.titre) || `Saison ${numero}`,
    episodes,
  };
}

/**
 * Une œuvre, quel que soit ce que le serveur a répondu.
 *
 * Rend `null` sans identifiant : une œuvre anonyme ne peut ni s'ouvrir, ni se
 * reprendre, ni s'annoncer. Mieux vaut l'écarter de la grille que d'y poser une
 * carte qui ne mène nulle part.
 */
export function normaliserOeuvre(brut) {
  const id = texteOuVide(brut?.id);
  if (!id) return null;

  const saisons = (Array.isArray(brut.saisons) ? brut.saisons : [])
    .map(normaliserSaison)
    .filter((s) => s.episodes.length)
    .sort((a, b) => a.numero - b.numero);

  // ⚠️ DEUX VOCABULAIRES POUR LES MÊMES BORNES, ET C'EST VOULU.
  //
  // Le contrat HTTP dit `premier`/`dernier` ; la configuration du serveur, elle,
  // parle de `debut`/`fin` (voir `catalogue.js`, `normaliserCollection`). Les
  // deux se croisent selon la route empruntée et selon le moment — les deux
  // fichiers avancent en parallèle. Accepter les deux coûte une ligne ; n'en
  // accepter qu'un donne une grille d'épisodes VIDE le jour où l'autre arrive,
  // sans rien dans la console pour dire pourquoi.
  //
  // Même chose pour le hors-trame : `canon: false` côté configuration, `filler`
  // dans le vocabulaire d'usage.
  const collections = (Array.isArray(brut.collections) ? brut.collections : [])
    .map((c) => ({
      id: texteOuVide(c?.id) || null,
      titre: texteOuVide(c?.titre ?? c?.nom) || 'Sans titre',
      partie: nombreOuNul(c?.partie),
      premier: nombreOuNul(c?.premier ?? c?.debut),
      dernier: nombreOuNul(c?.dernier ?? c?.fin),
      filler: c?.filler === true || c?.canon === false,
      visuel: texteOuVide(c?.visuel ?? c?.image) || null,
    }))
    .filter((c) => c.titre);

  return {
    id,
    titre: texteOuVide(brut.titre) || 'Sans titre',
    // Un type inconnu vaut « série » : c'est la forme qui a des épisodes, donc
    // celle qui ne perd rien. Traiter une série comme un film, à l'inverse,
    // rendrait ses épisodes inaccessibles.
    type: TYPES.has(brut.type) ? brut.type : 'serie',
    affiche: texteOuVide(brut.affiche) || null,
    fond: texteOuVide(brut.fond) || null,
    note: nombreOuNul(brut.note),
    annee: anneeDeTexte(brut.annee),
    // ⚠️ CE QUE `releaseInfo` PORTE VRAIMENT : une plage, pas une date.
    //
    // « 2026- » = en cours. « 2006-2010 » = terminee. « 2019 » = un film, ou une
    // saison unique. Le protocole met les trois dans le meme champ, et on n'en
    // lisait que le premier nombre — c'est-a-dire l'annee de DEBUT, la seule
    // qui ne dise rien de ce qui sort en ce moment.
    // `periode` = le `releaseInfo` brut d'AIOMetadata (« 2021- »), que le mix
    // garde depuis le 20/10 : `annee` n'en porte que le premier nombre, et
    // une série en cours passait pour finie l'année de son début (MESURÉ sur
    // les captures du 12/09 : 0 œuvre « en cours » sur 117).
    anneeFin: anneeDeFin(brut.periode ?? brut.annee),
    enCours: estEnCours(brut.periode ?? brut.annee),
    // Les dates de diffusion (ms), toutes venues d'AIOMetadata : `sortie` est
    // la première diffusion de l'œuvre (catalogue), les trois autres sont lues
    // dans la fiche (épisodes datés) et gravées par `oeuvres.js`.
    sortie: dateOuNul(brut.sortie),
    debutSaison: dateOuNul(brut.debutSaison),
    derniereSortie: dateOuNul(brut.derniereSortie),
    prochaineSortie: dateOuNul(brut.prochaineSortie),
    statut: texteOuVide(brut.statut) || null,
    genres: listeDeTextes(brut.genres),
    // L'étagère où l'accueil range l'œuvre (« series », « films », « live »…).
    // Elle est LIBRE côté serveur : on la garde telle quelle, sans la traduire
    // en catégorie fermée — c'est très exactement ce que faisaient les trois
    // planches de l'ancien accueil, et ce qu'on vient de démonter.
    rayon: texteOuVide(brut.rayon) || null,
    // Depuis quand une source rend un lien pour cette œuvre. `null` veut dire
    // « on ne sait pas encore » — l'examen de fond n'est peut-être pas passé —
    // et pas « indisponible » : la rangée des nouveautés fait la différence.
    dispoDepuis: nombreOuNul(brut.dispoDepuis),
    // Le serveur a servi de mémoire faute de source : l'écran le dit plutôt que
    // de laisser croire que l'œuvre n'a pas d'épisodes.
    sansSource: brut.sansSource === true,
    // Les épisodes viennent du miroir : la source ne répond plus depuis
    // `miroir.depuis`, et ceux sortis depuis manquent. L'écran le dit.
    miroir: brut.miroir && typeof brut.miroir === 'object'
      ? { depuis: nombreOuNul(brut.miroir.depuis), souci: texteOuVide(brut.miroir.souci) || null } : null,
    // Le classement des animes, tel que le serveur l'envoie. `sousTrisAnime` et
    // `chercherOeuvres` le lisent sous cette forme : le perdre ici viderait le
    // menu « Séries / Films » du rayon des animes sans autre symptôme.
    ...(brut.anime && typeof brut.anime === 'object' ? { anime: {
      format: texteOuVide(brut.anime.format) || null,
      saison: texteOuVide(brut.anime.saison) || null,
      statut: texteOuVide(brut.anime.statut) || null,
    } } : {}),
    // Le rang de MIX, quand le catalogue en porte un (`mix.js`, lot suivant) :
    // c'est lui qui fait l'ordre « Sélection ». Sans lui, c'est la position
    // dans la liste — le perdre ici ferait retomber la grille sur l'ordre du
    // fichier sans rien dire.
    ...(brut.mix && typeof brut.mix === 'object' ? { mix: {
      source: texteOuVide(brut.mix.source) || null,
      rang: nombreOuNul(brut.mix.rang),
      nouveaute: brut.mix.nouveaute === true,
    } } : {}),
    resume: texteOuVide(brut.resume),
    // La durée d'un FILM, en secondes. Hors contrat mais servie par
    // `/api/oeuvre/:id` : une œuvre en une pièce n'a pas d'épisode où
    // l'accrocher, et sans elle la fiche d'un film ne dit pas combien de temps
    // il dure — ce qui est la deuxième question qu'on se pose devant un film.
    duree: secondes(brut.duree),
    saisons,
    collections,
    // À plat, dans l'ordre de l'histoire : c'est sous cette forme que se
    // cherchent un voisin, un suivant, une reprise.
    episodes: saisons.flatMap((s) => s.episodes),
  };
}

/** Le catalogue entier, les entrées illisibles écartées. */
export function normaliserCatalogue(liste) {
  return (Array.isArray(liste) ? liste : []).map(normaliserOeuvre).filter(Boolean);
}

// ---------------------------------------------------------------------------
// Le pont vers l'ancienne forme
// ---------------------------------------------------------------------------

/**
 * Les épisodes d'une collection (un « arc »).
 *
 * `premier` et `dernier` désignent des numéros ABSOLUS quand la série en a —
 * c'est ainsi qu'on découpe une longue série, « du 1 au 20 ». Sans numérotation
 * absolue, on retombe sur le numéro dans la saison, ce qui reste juste tant que
 * la collection ne franchit pas une frontière de saison.
 *
 * Bornes absentes : la collection prend tout. C'est le cas d'une œuvre qui n'a
 * qu'un seul bloc, et le rendu reste correct.
 */
function episodesDeCollection(episodes, collection) {
  const { premier, dernier } = collection;
  if (!premier && !dernier) return [...episodes];

  // ⚠️ LES BORNES SONT DES NUMÉROS ABSOLUS, ET LES SPÉCIAUX N'EN ONT PAS.
  //
  // Le serveur calcule `premier`/`dernier` sur les seuls numéros absolus des
  // épisodes RÉGULIERS (voir `collections.js`) ; la saison 0 — OAV, récaps —
  // en est exclue et ses épisodes rendent `absolu: null`. Comparer alors leur
  // numéro de saison aux bornes absolues rangeait le « spécial 1 » dans l'arc
  // qui couvre les épisodes 1 à 20 : un OAV au milieu de la trame, sans que
  // rien ne le signale.
  //
  // Dès qu'une numérotation absolue existe, on ne filtre QUE sur elle. Sur une
  // œuvre qui n'en a aucune, le numéro dans la saison reprend le rôle — c'est
  // la seule échelle disponible, et elle est juste tant qu'une collection ne
  // franchit pas une frontière de saison.
  const parAbsolu = episodes.some((e) => e.absolu > 0);
  return episodes.filter((e) => {
    const n = parAbsolu ? e.absolu : e.numero;
    if (!(n > 0)) return false;
    if (premier && n < premier) return false;
    if (dernier && n > dernier) return false;
    return true;
  });
}

/**
 * La durée typique d'un épisode : la MÉDIANE, pas la moyenne.
 *
 * Un épisode double ou un récapitulatif de six minutes tirent une moyenne ; la
 * médiane, elle, tient. Sert d'estimation au lecteur avant que la vraie durée
 * du fichier soit connue — jamais d'affichage définitif.
 */
export function dureeTypique(oeuvre) {
  const durees = (oeuvre?.episodes ?? []).map((e) => e.duree).filter((d) => d > 0).sort((a, b) => a - b);
  if (!durees.length) return 0;
  return durees[Math.floor(durees.length / 2)];
}

/**
 * L'œuvre, telle que le reste du client sait la lire.
 *
 * ⚠️ CETTE FONCTION EXISTE POUR NE PAS RÉÉCRIRE QUARANTE LECTURES.
 * `serie.arcs[].episodes[].season` / `.episode` / `.title` / `.thumbnail` est
 * la forme qu'attendent `menu-episodes.js`, la grille, le menu des arcs, les
 * voisins du lecteur, la recherche interne et l'annonce dans le salon. Le
 * contrat du serveur, lui, dit `saisons[].episodes[].numero` / `.titre` /
 * `.vignette`. Traduire une fois ici, c'est garder UNE seule forme dans le
 * client — celle d'avant — et n'avoir qu'un endroit à corriger le jour où le
 * contrat bouge.
 *
 * Ce n'est PAS un retour en arrière vers « une seule série » : ce qu'on obtient
 * est l'œuvre OUVERTE, et il y en a autant que le catalogue en compte.
 */
export function enSerie(oeuvre) {
  if (!oeuvre) return null;

  const enVideo = (e) => ({
    season: e.saison,
    episode: e.numero,
    absolu: e.absolu,
    title: e.titre,
    resume: e.resume,
    duree: e.duree,
    released: e.released,
    thumbnail: e.vignette,
    langues: e.langues,
  });

  // Les collections font les arcs quand il y en a ; sinon chaque saison en
  // devient un. Une œuvre sans découpage aurait un menu d'arcs vide et une
  // grille qui ne s'affiche jamais — `rendreArc(0)` n'ayant rien à rendre.
  const arcs = (oeuvre.collections.length
    ? oeuvre.collections.map((c) => ({
      nom: c.titre,
      partie: c.partie,
      filler: c.filler,
      image: c.visuel,
      episodes: episodesDeCollection(oeuvre.episodes, c).map(enVideo),
    }))
    : oeuvre.saisons.map((s) => ({
      nom: s.titre,
      partie: null,
      filler: false,
      image: null,
      episodes: s.episodes.map(enVideo),
    }))
  ).filter((a) => a.episodes.length);

  return {
    id: oeuvre.id,
    name: oeuvre.titre,
    type: oeuvre.type,
    poster: oeuvre.affiche,
    background: oeuvre.fond,
    description: oeuvre.resume,
    annee: oeuvre.annee,
    note: oeuvre.note,
    genres: oeuvre.genres,
    trailer: null,
    // `secondesDe()` relit cette chaîne pour donner au lecteur une durée
    // approchée avant la mesure réelle : elle doit donc s'écrire comme il sait
    // la lire — « 24 min », « 1 h 45 min ».
    runtime: dureeLisible(dureeTypique(oeuvre)),
    videos: oeuvre.episodes.map(enVideo),
    arcs,
  };
}

// ---------------------------------------------------------------------------
// Chercher et filtrer dans le catalogue
// ---------------------------------------------------------------------------

/** Tous les genres du catalogue, dédoublonnés, dans l'ordre alphabétique. */
export function genresDuCatalogue(oeuvres) {
  const vus = new Map();
  for (const o of oeuvres ?? []) {
    for (const g of o.genres ?? []) {
      // La casse varie d'une source à l'autre (« Action » / « action ») : on
      // dédoublonne sur la forme normalisée et on garde le premier libellé vu.
      const cle = normaliserRecherche(g);
      if (cle && !vus.has(cle)) vus.set(cle, g);
    }
  }
  return [...vus.values()].sort((a, b) => a.localeCompare(b, 'fr'));
}

/**
 * Les œuvres qui répondent à ce qu'on cherche.
 *
 * Trois filtres indépendants, et c'est voulu : on peut chercher « guerre » dans
 * les seuls films d'action. Les combiner en un seul champ obligerait à inventer
 * une syntaxe que personne n'a demandée.
 *
 * La note de pertinence suit la même règle que la recherche d'épisodes
 * (`chercherDansCatalogue`) : titre exact, puis titre commençant par la
 * requête, puis requête perdue au milieu, puis genre ou résumé. À note égale,
 * l'ordre du tri demandé — jamais l'ordre du hasard, sinon les cartes sautent
 * sous le doigt pendant qu'on finit de taper.
 */
/**
 * Les années présentes dans le catalogue, de la plus récente à la plus ancienne.
 *
 * Comme les genres : proposer 1974 quand rien ne date de 1974 donne un filtre
 * qui ne rend rien, et l'on cherche alors la panne du mauvais côté.
 */
export function anneesDuCatalogue(oeuvres) {
  const vues = new Set();
  for (const o of oeuvres ?? []) if (o?.annee) vues.add(o.annee);
  return [...vues].sort((a, b) => b - a);
}

// ---------------------------------------------------------------------------
// LES SAISONS D'ANIME ET LA DIFFUSION (Toji, 20/10 : « par sortie de saison :
// tous ceux qui sont de cette saison actuellement, tous ceux qui sortent
// actuellement », « Simulcasts »).
//
// Hiver = janvier–mars, Printemps = avril–juin, Été = juillet–septembre,
// Automne = octobre–décembre — le découpage d'AniList et de Crunchyroll.
// ---------------------------------------------------------------------------
const NOMS_TRIMESTRE = ['Hiver', 'Printemps', 'Été', 'Automne'];
const JOUR = 86_400_000;
/** Une sortie ou une annonce à moins de trois semaines « est en cours ». */
const FENETRE_DIFFUSION = 21 * JOUR;

/** « Automne 2026 » pour une date (ms), ou `null`. */
export function trimestreDe(t) {
  if (!Number.isFinite(t)) return null;
  const d = new Date(t);
  return `${NOMS_TRIMESTRE[Math.floor(d.getUTCMonth() / 3)]} ${d.getUTCFullYear()}`;
}

/** La saison qui situe l'œuvre : celle de sa saison la plus récente, sinon de sa première diffusion. */
export const trimestreDeLOeuvre = (o) => trimestreDe(o?.debutSaison ?? o?.sortie);

/** Ce que l'œuvre fait en ce moment, d'après ses dates (`maintenant` en ms). */
export function diffusionDe(o, maintenant = Date.now()) {
  const recent = o?.derniereSortie && maintenant - o.derniereSortie <= FENETRE_DIFFUSION;
  const imminent = o?.prochaineSortie && o.prochaineSortie - maintenant <= FENETRE_DIFFUSION;
  // `mix.nouveaute` = l'œuvre vient du catalogue « en diffusion » (MAL airing).
  const enDiffusion = Boolean(recent || imminent || o?.mix?.nouveaute
    || (o?.enCours && /airing|continuing|en cours|ongoing|currently/i.test(o?.statut ?? '')));
  // Commencée (pas seulement annoncée) dans la saison d'anime actuelle.
  const debut = o?.debutSaison ?? o?.sortie;
  const saisonEnCours = Boolean(debut && debut <= maintenant) && trimestreDe(debut) === trimestreDe(maintenant);
  const aVenir = Boolean((o?.sortie && o.sortie > maintenant) || (o?.prochaineSortie && !o?.derniereSortie));
  // ⚠️ « DISPONIBLE MAINTENANT » : au moins un épisode SORTI. Une saison
  // annoncée dont rien n'a encore été diffusé n'est pas un simulcast.
  const sorti = !aVenir && (Boolean(o?.derniereSortie) || Boolean(o?.dispoDepuis) || !o?.prochaineSortie);
  return {
    enDiffusion,
    saisonEnCours,
    aVenir,
    simulcast: o?.type === 'anime' && enDiffusion && sorti && (saisonEnCours || Boolean(o?.mix?.nouveaute)),
  };
}

const DIFFUSIONS = {
  'saison-en-cours': (d) => d.saisonEnCours,
  'en-diffusion': (d) => d.enDiffusion,
  simulcasts: (d) => d.simulcast,
  'a-venir': (d) => d.aVenir,
};

/** Les saisons présentes, de la plus récente à la plus ancienne. */
export function trimestresDuCatalogue(oeuvres) {
  const vues = new Map();
  for (const o of oeuvres ?? []) {
    const t = o?.debutSaison ?? o?.sortie;
    const nom = trimestreDe(t);
    if (!nom) continue;
    const d = new Date(t);
    vues.set(nom, d.getUTCFullYear() * 4 + Math.floor(d.getUTCMonth() / 3));
  }
  return [...vues.entries()].sort((a, b) => b[1] - a[1]).map(([nom]) => nom);
}

/** Les décennies couvertes par une liste d'années, de la plus récente à la plus ancienne. */
export function decenniesDe(annees) {
  return [...new Set((annees ?? []).map((a) => Math.floor(a / 10) * 10))].sort((a, b) => b - a);
}

const derniereDate = (o, maintenant) => o.derniereSortie
  ?? (o.sortie && o.sortie <= maintenant ? o.sortie : 0);

export function chercherOeuvres(oeuvres, {
  // ⚠️ `tri` EST UNE VALEUR, PAS UNE LISTE : `mix` (l'ordre du catalogue),
  // `titre`, `annee` ou `note`. Un tableau recu se lit par son premier element,
  // un critere inconnu retombe sur `mix` — voir `parTri` plus bas.
  q = '', type = 'tout', genre = 'tout', tri = 'mix', annee = 'tout',
  // Le sous-tri des animes. Il ne mord QUE sur le rayon des animes : demander
  // « film » en regardant les series n'a pas de sens, et laisser le critère
  // agir ailleurs viderait la grille sans qu'on comprenne pourquoi.
  format = 'tout', saison = 'tout', statut = 'tout',
  // La saison d'anime (« Automne 2026 », tous rayons) et ce que l'œuvre fait
  // en ce moment (`saison-en-cours`, `en-diffusion`, `simulcasts`, `a-venir`).
  trimestre = 'tout', diffusion = 'tout', maintenant = Date.now(),
  // ⚠️ CE QU'ON MONTRE N'EST PAS FORCÉMENT CE QU'ON PEUT LIRE.
  //
  // Toji : « ça affiche tout avec l'aperçu et tout le reste, mais ça ne met
  // pas ». Le catalogue vient des métadonnées : il connaît des titres qu'aucune
  // source ne rend. Une affiche, un synopsis, une bande-annonce — et un clic
  // qui ne mène nulle part.
  //
  // `dispoDepuis` est ce que l'examen de fond a CONSTATÉ : une source a rendu
  // un lien. Ce garde-fou n'est pas allumé d'office — tant que l'examen n'a pas
  // fait le tour, il masquerait des titres parfaitement lisibles.
  dispoSeulement = false,
} = {}) {
  const requete = normaliserRecherche(q);
  const genreVoulu = genre && genre !== 'tout' ? normaliserRecherche(genre) : null;
  const surLesAnimes = type === 'anime';

  const retenues = [];
  // La position dans la liste, relevee AVANT tout filtre : c'est le repli de
  // l'ordre « mix » quand l'œuvre ne porte pas de rang.
  let position = -1;
  for (const o of oeuvres ?? []) {
    position += 1;
    if (dispoSeulement && !o.dispoDepuis) continue;
    if (type && type !== 'tout' && o.type !== type) continue;
    // L'année demandée, quand elle l'est. « recentes » couvre l'année en cours
    // et la précédente : c'est ce qu'on entend par « ce qui sort en ce moment »,
    // et le calculer ici évite de figer 2025 et 2026 dans du code qui vivra
    // plus longtemps qu'eux.
    //
    // ⚠️ UNE SÉRIE COUVRE TOUTES SES ANNÉES DE DIFFUSION, PAS SEULEMENT LA
    // PREMIÈRE. Le filtre comparait l'année de DÉBUT : « 2023- » (les Carnets
    // de l'apothicaire, saison 3 en cours en 2026) sortait du filtre 2026, et
    // « 2006–2010 » n'apparaissait qu'en 2006. Une œuvre en cours court
    // jusqu'à aujourd'hui ; une plage fermée, jusqu'à sa fin.
    if (annee && annee !== 'tout') {
      const cetteAnnee = new Date().getFullYear();
      const fin = o.enCours ? cetteAnnee : (o.anneeFin ?? o.annee);
      if (annee === 'recentes') {
        if (!(fin >= cetteAnnee - 1)) continue;
      } else if (/^d\d{4}$/.test(annee)) {
        // Une décennie (« d2010 ») : l'œuvre y a au moins une année de diffusion.
        const de = Number(annee.slice(1));
        if (!(o.annee <= de + 9 && de <= fin)) continue;
      } else if (!(o.annee <= Number(annee) && Number(annee) <= fin)) continue;
    }
    if (genreVoulu && !(o.genres ?? []).some((g) => normaliserRecherche(g) === genreVoulu)) continue;
    if (trimestre && trimestre !== 'tout' && trimestreDeLOeuvre(o) !== trimestre) continue;
    if (diffusion && diffusion !== 'tout' && DIFFUSIONS[diffusion]
      && !DIFFUSIONS[diffusion](diffusionDe(o, maintenant))) continue;

    if (surLesAnimes) {
      const c = classementAnime(o);
      // Deux formes seulement — « serie » ou « film » — quelle que soit la
      // valeur brute que la source a declaree (TV, Movie, OVA…).
      if (format !== 'tout' && formeAnime(o) !== format) continue;
      if (saison !== 'tout' && String(c.saison ?? '') !== saison) continue;
      if (statut !== 'tout' && String(c.statut ?? '') !== statut) continue;
    }

    let note = 1;
    if (requete) {
      const titre = normaliserRecherche(o.titre);
      const resume = normaliserRecherche(o.resume);
      const genres = (o.genres ?? []).map(normaliserRecherche);
      if (titre === requete) note = 100;
      else if (titre.startsWith(requete)) note = 70;
      else if (titre.includes(requete)) note = 40;
      else if (genres.some((g) => g.includes(requete))) note = 20;
      else if (resume.includes(requete)) note = 10;
      else note = 0;
    }
    if (note) retenues.push({ oeuvre: o, note, position });
  }

  // L'annee qui situe reellement une oeuvre dans le temps : sa fin quand elle
  // en a une, son debut sinon.
  // ⚠️ EN COURS = CETTE ANNÉE. Ranger « 2023- » en 2023 faisait passer une
  // série diffusée cette semaine derrière tout ce qui s'est terminé depuis.
  const anneeUtile = (o) => (o.enCours ? (o.annee ? new Date().getFullYear() : 0) : (o.anneeFin ?? o.annee ?? 0));
  // L'ordre « Sélection » : le rang de MIX quand le catalogue en porte un, la
  // position dans la liste sinon. Deux œuvres n'ont jamais la meme position,
  // donc cet ordre est total : rien ne se rebat d'une peinture a l'autre.
  const rangMix = (r) => (Number.isFinite(r.oeuvre.mix?.rang) ? r.oeuvre.mix.rang : r.position);

  // ⚠️ UN SEUL CRITERE, ET « RECENT » N'EXISTE PLUS.
  //
  // Le tri « recent » lisait `dispoDepuis`, que l'examen de fond repose a
  // chaque passe : un constat de plus, et le rayon se rebattait sous les yeux
  // de la personne. Toji (12/09) : « je ne touche a rien et je vois les
  // affiches changer de place, ca change en continu — ce n'etait pas du tout
  // l'objectif ». Ce qui « sort en ce moment » est desormais l'affaire du
  // catalogue lui-meme (le MIX, recompose a heure fixe), pas d'un tri qui
  // bouge a chaque constat. `dispoDepuis` n'entre plus dans AUCUN tri : il
  // alimente le badge et le filtre « disponible seulement », c'est tout.
  //
  // Et un seul critere a la fois : « a la place du multi-tri, fais simplement
  // un tri avec un menu dropdown ». Les ex aequo se departagent par l'ordre
  // du catalogue, puis par le titre — un ordre stable, sans regle cachee.
  const parTri = {
    mix: (a, b) => rangMix(a) - rangMix(b),
    titre: (a, b) => a.oeuvre.titre.localeCompare(b.oeuvre.titre, 'fr'),
    // La derniere annee qui compte : celle de FIN pour ce qui est termine,
    // celle de debut pour le reste. Une œuvre sans annee vaut 0 : elle ferme
    // la marche plutot que de remonter en tete d'un tri qu'elle ne merite pas.
    annee: (a, b) => anneeUtile(b.oeuvre) - anneeUtile(a.oeuvre),
    note: (a, b) => (b.oeuvre.note ?? 0) - (a.oeuvre.note ?? 0),
    // « Dernières sorties » : le dernier épisode sorti d'abord, sinon la
    // première diffusion si elle est passée. Sans date, en dernier.
    sorties: (a, b) => derniereDate(b.oeuvre, maintenant) - derniereDate(a.oeuvre, maintenant),
  };
  // Un tableau recu — une page ouverte depuis longtemps — se lit par son
  // premier element ; le reste ne cumule rien.
  const demande = Array.isArray(tri) ? tri[0] : tri;
  const critere = parTri[demande] ?? parTri.mix;
  const ordre = (a, b) => critere(a, b)
    || parTri.mix(a, b)
    || a.oeuvre.titre.localeCompare(b.oeuvre.titre, 'fr');
  // ⚠️ UN TRI CHOISI S'APPLIQUE AUSSI PENDANT UNE RECHERCHE. La pertinence
  // passait devant tout : « A→Z » ou « Note » avec un mot tapé ne changeait
  // rien à l'ordre — le menu semblait cassé. « Sélection » garde la pertinence
  // en tête ; un tri explicite tranche, la pertinence départage.
  const triExplicite = demande !== 'mix' && Boolean(parTri[demande]);
  retenues.sort((a, b) => (triExplicite
    ? critere(a, b) || b.note - a.note || ordre(a, b)
    : b.note - a.note || ordre(a, b)));
  return retenues.map((r) => r.oeuvre);
}

/**
 * Les nouveautés : les plus récentes, et rien d'autre.
 *
 * Une œuvre sans année n'y figure PAS. Elle serait rangée en 0 et arriverait
 * donc en dernier — ou, pire, en premier selon le sens du tri — alors qu'on ne
 * sait simplement pas quand elle est sortie. Une rangée « Nouveautés » qui
 * contient un titre de 1998 parce que son année manque ne dit plus rien.
 */

/**
 * La fenêtre de lignes à peindre dans une grille virtualisée.
 *
 * ⚠️ CE CALCUL EST LA MOITIÉ DU TRAVAIL, ET LA SEULE QUI PUISSE ÊTRE ÉPROUVÉE.
 *
 * L'autre moitié — mesurer les colonnes, poser le rembourrage, rebrancher les
 * clics — vit dans le DOM. Celle-ci n'a besoin de rien : des nombres entrent,
 * deux nombres sortent. C'est ici que se cachent les erreurs qui se voient (une
 * ligne blanche en haut, un saut au défilement), et c'est donc ici qu'on les
 * cherche.
 *
 * `marge` est le nombre de lignes gardées EN PLUS de part et d'autre.
 *
 * ⚠️ SIX, ET PAS TROIS. Toji : « quand je descends progressivement, les icônes
 * ne s'affichent pas suffisamment rapidement … on met 6 lignes en dessous et 6
 * lignes au-dessus … même quand on descend très vite. Pour un utilisateur, il
 * ne doit pas voir qu'il y a du chargement. »
 *
 * Trois lignes de marge, c'est environ un demi-écran d'avance : un défilement
 * rapide le mange en une fraction de seconde, et l'on rattrape des cases vides.
 * Six, avec deux lignes visibles, font quatorze lignes montées en permanence —
 * de quoi absorber un coup de molette entier avant que la peinture suivante
 * n'arrive.
 */
export function fenetreDeGrille({
  scrollTop = 0, hauteurVue = 800, hautGrille = 0, hauteurLigne = 200, lignes = 0, marge = 6,
} = {}) {
  if (!(lignes > 0) || !(hauteurLigne > 0)) return { premiere: 0, derniere: -1 };
  // Ce qui a déjà défilé SOUS le haut de la grille. Négatif tant qu'on ne l'a
  // pas atteinte — on est alors au tout début, pas avant.
  const vu = Math.max(0, scrollTop - hautGrille);
  const premiere = Math.max(0, Math.floor(vu / hauteurLigne) - marge);
  const derniere = Math.min(lignes - 1, Math.ceil((vu + hauteurVue) / hauteurLigne) + marge);
  return { premiere, derniere };
}

export function nouveautes(oeuvres, combien = 12, { dispoSeulement = false } = {}) {
  // ⚠️ LE FILTRE « SEULEMENT CE QUI EST REGARDABLE » S'APPLIQUE AVANT LES TROIS
  // REGLES, PAS DANS L'UNE D'ELLES.
  //
  // Cette rangee etait la SEULE de l'accueil a ne pas le recevoir. Tant qu'elle
  // se batissait sur `dispoDepuis`, ca ne se voyait pas : elle ne montrait que
  // des titres constates disponibles. Depuis qu'elle lit d'abord `mix.nouveaute`
  // — un drapeau pose a la composition, qui ne dit rien de la disponibilite —
  // la premiere branche court-circuitait le filtre, et `?dispo=oui` rendait des
  // simulcasts dont aucune source ne rend de lien. Le poser ici couvre les trois
  // branches d'un coup, y compris le repli par annee qui ne filtrait rien.
  const liste = (oeuvres ?? []).filter((o) => o && (!dispoSeulement || o.dispoDepuis));

  // ⚠️ D'ABORD LE SIMULCAST, C'EST-A-DIRE CE QUE LE MIX A MARQUE.
  //
  // Toji (12/09) : les nouveautes, ce sont les series en cours de diffusion.
  // Le catalogue des simulcasts le dit a la source (`mal.airing`), et
  // l'instantane (`instantane.js`) pose ce drapeau sur chaque oeuvre concernee
  // au moment de composer. On le LIT, on ne le recalcule pas : la rangee est
  // alors la meme pour tout le monde et ne bouge plus entre deux
  // recompositions — ce qui est tout l'objet du lot.
  //
  // L'ordre est celui du mix, c'est-a-dire celui de la liste : la trier par
  // date rebattrait les affiches a chaque constat de disponibilite, et c'est
  // exactement le defaut qu'on vient de supprimer.
  //
  // Sans aucun drapeau — un catalogue ecrit a la main, une installation qui
  // n'a pas encore compose son premier mix — on retombe sur les deux regles
  // historiques, plus bas.
  const marquees = liste.filter((o) => o?.mix?.nouveaute === true);
  if (marquees.length) return marquees.slice(0, Math.max(0, combien));

  // ⚠️ UNE NOUVEAUTE, C'EST CE QUI VIENT D'ARRIVER — PAS CE QUI VIENT DE SORTIR.
  //
  // Cette rangée triait le catalogue par année. Elle mettait donc en tête les
  // films annoncés pour l'an prochain — « il y a Avengers Doomsday, sauf qu'en
  // fait il est pas dispo » — et presque aucune série, puisqu'une série porte
  // l'année de son PREMIER épisode. Une vitrine qui montre ce qu'on ne peut pas
  // regarder fait cliquer pour rien.
  //
  // `dispoDepuis` est le moment où l'examen de fond a constaté qu'une source
  // rendait un lien. C'est donc « arrivé chez nous le … », et c'est ce que la
  // rangée doit dire.
  const arrivees = liste
    .filter((o) => o.dispoDepuis)
    .sort((a, b) => b.dispoDepuis - a.dispoDepuis || a.titre.localeCompare(b.titre, 'fr'));
  if (arrivees.length) return arrivees.slice(0, Math.max(0, combien));

  // Repli : tant que l'examen n'a rien constaté — installation neuve, sources
  // muettes — on retombe sur l'année. Une rangée vide dirait « il n'y a rien de
  // nouveau », ce qui est une autre information, et fausse.
  return liste
    .filter((o) => o.annee)
    .sort((a, b) => b.annee - a.annee || a.titre.localeCompare(b.titre, 'fr'))
    .slice(0, Math.max(0, combien));
}

// ---------------------------------------------------------------------------
// Reprendre une lecture
// ---------------------------------------------------------------------------

/** La part lue, entre 0 et 1. Sans durée connue, zéro — jamais une jauge fausse. */
export function partLue(position, duree) {
  const p = secondes(position);
  const d = secondes(duree);
  if (!d) return 0;
  return Math.min(1, Math.max(0, p / d));
}

/**
 * Les entrées de « Reprendre », prêtes à peindre.
 *
 * Le serveur rend le strict nécessaire ; le catalogue, lui, connaît l'affiche
 * et le type. On les rapproche ici pour que la rangée d'accueil n'ait plus qu'à
 * dessiner.
 *
 * ⚠️ LA RANGÉE NE SE VIDE JAMAIS D'ELLE-MÊME (Toji, 22/10) : seule la croix
 * retire. Rien n'est donc écarté ici, ni fini (barre pleine, `vu`), ni absent
 * du catalogue CHARGÉ. Ce filtre-là faisait disparaître VisionQuest (titre
 * ouvert depuis la recherche, hors catalogue) dès que le catalogue arrivait
 * avant `/api/reprendre` — c'est-à-dire à chaque retour à l'accueil : le
 * catalogue du navigateur n'est qu'une page, pas la liste de ce qui s'ouvre.
 * Le serveur, lui, sait ce qui s'ouvre (`existe` dans `/api/reprendre`).
 */
export function normaliserReprises(entrees, oeuvres = []) {
  const parId = new Map((oeuvres ?? []).map((o) => [o.id, o]));
  const sorties = [];
  for (const e of Array.isArray(entrees) ? entrees : []) {
    const oeuvreId = texteOuVide(e?.oeuvreId);
    if (!oeuvreId) continue;
    const oeuvre = parId.get(oeuvreId) ?? null;

    const position = secondes(e.position);
    const duree = secondes(e.duree);
    const vu = e.vu === true;
    const part = vu ? 1 : partLue(position, duree);

    sorties.push({
      oeuvreId,
      oeuvre,
      titre: texteOuVide(e.titre) || oeuvre?.titre || 'Sans titre',
      // Le titre de l'ÉPISODE, quand le serveur le joint. `titre` porte celui
      // de l'œuvre : une carte « Reprendre » qui ne dit que « Bleach » ne
      // rappelle pas où l'on en était.
      episodeTitre: texteOuVide(e.episodeTitre) || null,
      saison: nombreOuNul(e.saison),
      episode: nombreOuNul(e.episode),
      position,
      duree,
      part,
      vignette: texteOuVide(e.vignette) || oeuvre?.fond || oeuvre?.affiche || null,
      type: oeuvre?.type ?? (nombreOuNul(e.episode) ? 'serie' : 'film'),
      quand: nombreOuNul(e.quand),
      suivant: e.suivant === true,
      // Sorti depuis que l'on a fini l'épisode d'avant : la carte le dit.
      nouveau: e.nouveau === true,
      vu,
    });
  }
  return sorties;
}

/**
 * La seconde où reprendre : celle du serveur, sauf si CET appareil a retenu le
 * même épisode plus tard (le dernier envoi perdu par une sortie brutale).
 * `locale` = `{ saison, episode, position, quand }` ; pure.
 */
export function positionDeReprise(entree, locale) {
  // Fini (barre pleine) : rouvrir, c'est revoir depuis le début.
  if (entree?.vu) return 0;
  const serveur = secondes(entree?.position);
  if (!locale || entree?.suivant) return serveur;
  const meme = (Number(locale.saison) || 0) === (Number(entree?.saison) || 0)
    && (Number(locale.episode) || 0) === (Number(entree?.episode) || 0);
  const plusRecente = (Number(locale.quand) || 0) >= (Number(entree?.quand) || 0);
  return meme && plusRecente && secondes(locale.position) > 0 ? secondes(locale.position) : serveur;
}

/** Le `souci` que `miroir.js` pose quand la source a seulement dépassé sa patience. */
export const MIROIR_TARDE = 'la source tarde';
// 24 h : la source n'ajoute un épisode qu'à sa diffusion (au plus un par jour
// pour une série), et un miroir confirmé dans la journée contient donc ce
// qu'elle dirait — le rafraîchissement de fond, lui, corrige en secondes.
export const MIROIR_FRAIS_MS = 24 * 3600_000;

/**
 * Le bandeau « liste d'épisodes de secours » se montre-t-il ? Oui pour une vraie
 * panne — source muette, vide, ou miroir confirmé il y a plus de 24 h ; non
 * quand la source a seulement tardé sur un miroir récent. PURE.
 */
export function miroirADire(miroir, maintenant = Date.now()) {
  if (!miroir) return false;
  if (miroir.souci !== MIROIR_TARDE) return true;
  return !(miroir.depuis > 0 && maintenant - miroir.depuis < MIROIR_FRAIS_MS);
}

/**
 * L'état d'un épisode dans la liste de la fiche : `vu` (barre pleine + « Vu »)
 * d'après `vus` (`{ "S1E4": horodatage }`, rendu par `/api/reprendre`), ou la
 * part lue de l'épisode en cours (`entree`, une entrée normalisée). PURE.
 */
export function etatDEpisode(vus, entree, saison, episode) {
  if (vus?.[`S${saison}E${episode}`]) return { vu: true, part: 1 };
  const meme = entree && Number(entree.saison) === saison && Number(entree.episode) === episode;
  if (meme && entree.vu) return { vu: true, part: 1 };
  return { vu: false, part: meme ? entree.part || 0 : 0 };
}

/**
 * Ce qu'une carte « Reprendre » écrit sous le titre : le repère et le titre de
 * l'épisode, rien d'autre. Toji (21/10) a retiré le temps restant — la fine
 * barre blanche de la vignette dit déjà où l'on en est.
 */
export function sousTitreDeReprise(entree) {
  if (!entree) return '';
  // « Nouvel épisode » a quitté cette ligne pour une pastille DANS la vignette
  // (`pastilleDeReprise`, 22/10) : écrit aux deux endroits, il se lisait deux fois.
  const bouts = [];
  if (entree.saison && entree.episode) bouts.push(`S${entree.saison}E${entree.episode}`);
  if (entree.episodeTitre) bouts.push(entree.episodeTitre);
  return bouts.join(' · ');
}

/**
 * La pastille d'une reprise revenue d'elle-même (`nouveau`, voir reprises.js) :
 * « Nouvelle saison » quand ce qui est sorti ouvre une saison après la
 * première (S2E1…), sinon « Nouvel épisode ». `null` pour tout le reste. PURE.
 * DÉDUIT de saison/épisode : le serveur ne dit pas d'où l'entrée a avancé.
 */
export function pastilleDeReprise(entree) {
  if (!entree?.nouveau) return null;
  return entree.saison > 1 && entree.episode === 1 ? 'Nouvelle saison' : 'Nouvel épisode';
}

/**
 * Le nom court d'une œuvre, pour les endroits qui comptent les caractères.
 *
 * Le catalogue rend le titre complet du fournisseur — « Bleach: Thousand-Year
 * Blood War - The Almighty » — dont il ne restait que la queue une fois borné à
 * 128 caractères dans la présence Discord : « TheAlmighty », qui ne désigne
 * rien pour personne. On coupe donc au premier séparateur de sous-titre.
 */
export function nomCourt(titre) {
  const t = texteOuVide(titre);
  if (!t) return null;
  return t.split(/\s*[:—–]\s*|\s+-\s+/)[0].trim() || t;
}
